import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ReactComponent as ArrowLeftIcon } from '../icons/arrow.left.svg';
import type { SocialCandidateEmail } from '../data/repositories/SocialLogin';
import {
  getSocialLoginErrorMessage,
  isSocialLoginSuccess,
  socialLoginCreateAccount,
  socialLoginRequestVerificationCode
} from '../data/repositories/SocialLogin';
import { completeAuthLogin } from '../utils/completeAuthLogin';

interface SocialAccountSelectionPageState {
  socialSessionToken?: string;
  candidateEmails?: SocialCandidateEmail[];
  continueWithNewAccountAvailable?: boolean;
  isPromotionAgreed?: boolean;
}

export default function SocialAccountSelectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as SocialAccountSelectionPageState | null) ?? null;

  const socialSessionToken = state?.socialSessionToken || '';
  const candidateEmails = Array.isArray(state?.candidateEmails) ? state.candidateEmails : [];
  const continueWithNewAccountAvailable = state?.continueWithNewAccountAvailable === true;
  const isPromotionAgreed = state?.isPromotionAgreed === true;

  const [socialLoadingKey, setSocialLoadingKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLoginButtonDisabled = socialLoadingKey !== null;

  const onBackClick = () => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate('/onboarding', { replace: true });
  };

  const handleSelectCandidateEmail = async (selectedEmail: string) => {
    if (!socialSessionToken) return;

    const loadingKey = `candidate:${selectedEmail}`;
    setErrorMessage(null);
    setSocialLoadingKey(loadingKey);

    try {
      const verificationResponse = await socialLoginRequestVerificationCode({
        socialSessionToken,
        selectedEmail
      });

      if (verificationResponse.success) {
        navigate('/login-with-code?init=true', {
          state: {
            email: verificationResponse.email || selectedEmail,
            maskedEmail: verificationResponse.maskedEmail,
            socialSessionToken,
            socialSelectedEmail: selectedEmail,
            isPromotionAgreed
          }
        });
        return;
      }

      setErrorMessage(
        getSocialLoginErrorMessage(verificationResponse) ||
          t('requestVerificationCodePage.socialVerificationCodeRequestFailedMessage')
      );
    } catch (_error) {
      setErrorMessage(t('requestVerificationCodePage.socialVerificationCodeRequestFailedMessage'));
    } finally {
      setSocialLoadingKey(null);
    }
  };

  const handleCreateNewAccount = async () => {
    if (!socialSessionToken) return;

    setErrorMessage(null);
    setSocialLoadingKey('create-account');

    try {
      const response = await socialLoginCreateAccount({
        socialSessionToken,
        isPromotionAgreed
      });

      if (isSocialLoginSuccess(response)) {
        const completed = completeAuthLogin({ response, navigate });
        if (!completed) {
          setErrorMessage(t('requestVerificationCodePage.socialCreateAccountFailedMessage'));
        }
        return;
      }

      setErrorMessage(
        getSocialLoginErrorMessage(response) ||
          t('requestVerificationCodePage.socialCreateAccountFailedMessage')
      );
    } catch (_error) {
      setErrorMessage(t('requestVerificationCodePage.socialCreateAccountFailedMessage'));
    } finally {
      setSocialLoadingKey(null);
    }
  };

  if (!socialSessionToken || candidateEmails.length === 0) {
    return (
      <div className="flex flex-col h-screen items-center justify-center px-[24px]">
        <span className="text-[15px] text-main-light-4 dark:text-main-dark-4">
          {t('requestVerificationCodePage.socialCandidateEmailsMissingMessage')}
        </span>
        <button
          type="button"
          className="mt-[20px] underline text-[14px]"
          onClick={() => navigate('/onboarding', { replace: true })}
        >
          {t('requestVerificationCodePage.socialExpandMethods')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark items-center w-[100vw] max-w-[570px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <button
        className="mt-[14px] px-[16px] mb-[15px] w-full z-50 justify-start"
        onClick={onBackClick}
        type="button"
      >
        <ArrowLeftIcon className="fill-black dark:fill-white" />
      </button>

      <div className="w-full max-w-[570px] px-[24px] flex flex-col gap-[12px]">
        <div className="flex flex-col mt-[12px] w-full mb-[8px]">
          <span className="text-left text-[24px] font-bold line-height-[120%]">
            {t('requestVerificationCodePage.socialSelectCandidateTitle')}
          </span>
          <span className="text-main-light-4 dark:text-main-dark-4 mt-2 text-[15px]">
            {t('requestVerificationCodePage.socialSelectCandidateDescription')}
          </span>
        </div>

        {candidateEmails.map((candidate) => {
          const loadingKey = `candidate:${candidate.email}`;
          const isCandidateLoading = socialLoadingKey === loadingKey;
          return (
            <button
              key={candidate.email}
              type="button"
              onClick={() => handleSelectCandidateEmail(candidate.email)}
              disabled={isLoginButtonDisabled}
              className="
                flex items-center justify-center
                rounded-[12px]
                min-h-[52px]
                border border-secondary-light dark:border-secondary-dark
                bg-white dark:bg-background-dark
                text-main-light-1 dark:text-main-dark-1
                text-[15px] font-semibold
                disabled:opacity-50
              "
            >
              {isCandidateLoading ? (
                <div
                  className="
                      animate-spin
                      w-4 h-4
                      border-[2px]
                      border-label-light dark:border-label-dark
                      border-t-primary-light dark:border-t-primary-dark
                      rounded-full
                    "
                />
              ) : (
                <span>{candidate.maskedEmail}</span>
              )}
            </button>
          );
        })}

        {continueWithNewAccountAvailable ? (
          <button
            type="button"
            onClick={handleCreateNewAccount}
            disabled={isLoginButtonDisabled}
            className="
                flex items-center justify-center
                rounded-[12px]
                min-h-[52px]
                border border-black
                bg-black
                text-white
                text-[15px] font-semibold
                disabled:opacity-50
                mt-[4px]
              "
          >
            {socialLoadingKey === 'create-account' ? (
              <div
                className="
                      animate-spin
                      w-4 h-4
                      border-[2px]
                      border-white/70
                      border-t-white
                      rounded-full
                    "
              />
            ) : (
              t('requestVerificationCodePage.socialContinueWithNewAccount')
            )}
          </button>
        ) : null}

        {errorMessage ? (
          <span className="text-[13px] text-red-500 mt-[4px]">{errorMessage}</span>
        ) : null}
      </div>
    </div>
  );
}
